// bm-design-system: collapsible disclosure primitive
//
// Built on native <details>/<summary> rather than a JS-driven panel: the browser
// gives correct disclosure semantics, keyboard handling and find-in-page for
// free, and the content stays reachable if JS never runs. The chevron is the
// only thing that needs styling — the marker is removed and replaced.
//
// Use it for a list of things where each entry has a summary worth scanning and
// a body worth hiding, e.g. the per-session path on an audit.
import * as React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export interface CollapsibleProps {
  /** The always-visible row. Interactive elements inside it will swallow clicks. */
  summary: React.ReactNode;
  children: React.ReactNode;
  /** Open on first render. Uncontrolled after that — the browser owns the state. */
  defaultOpen?: boolean;
  className?: string;
}

export function Collapsible({
  summary,
  children,
  defaultOpen = false,
  className,
}: CollapsibleProps) {
  return (
    <details
      open={defaultOpen}
      className={cn(
        "group overflow-hidden rounded-lg border border-hairline bg-page",
        className,
      )}
    >
      <summary
        className={cn(
          "flex cursor-pointer list-none items-center gap-3 px-4 py-3 transition-colors hover:bg-surface",
          // Safari still paints its own triangle without this.
          "[&::-webkit-details-marker]:hidden",
        )}
      >
        <ChevronRight
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-ink-muted transition-transform group-open:rotate-90"
        />
        <div className="min-w-0 flex-1">{summary}</div>
      </summary>
      <div className="border-t border-hairline px-4 py-3">{children}</div>
    </details>
  );
}

/**
 * Wraps a stack of Collapsibles so they read as one list rather than a set of
 * unrelated cards.
 */
export function CollapsibleGroup({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("space-y-2", className)}>{children}</div>;
}
